import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { be_regions, fr_regions, lu_regions } from "bundles/common/utils/constants";
import { formatRegionMessage } from "bundles/common/utils/utils";

// Mirrors Candidate#availability enum values.
export const AVAILABILITY_OPTIONS = [
  { label: "Available", value: "available" },
  { label: "Looking", value: "looking" },
  { label: "Listening", value: "listening" },
  { label: "Currently employed", value: "currently_employed" },
  { label: "Unavailable", value: "unavailable" },
];

export const COUNTRY_OPTIONS = [
  { label: "Belgium", value: "belgium" },
  { label: "France", value: "france" },
  { label: "Luxembourg", value: "luxembourg" },
];

const chipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "10px",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "all 0.15s ease",
  ...(active
    ? {
      backgroundColor: alpha("#079344", 0.12),
      color: "#079344",
      borderColor: "#079344",
      "&:hover": { backgroundColor: alpha("#079344", 0.18) },
    }
    : {
      backgroundColor: "rgba(255,255,255,0.9)",
      color: "#2A334E",
      borderColor: "rgba(42,51,78,0.2)",
      "&:hover": {
        backgroundColor: "#fff",
        borderColor: "rgba(42,51,78,0.35)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {def.grouped
            ? def.groups.map((group) => (
              <Box key={group.label}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {group.label}
                </Typography>
                {group.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(opt.value)}
                        onChange={() => handleToggle(opt.value)}
                        sx={{
                          color: "rgba(42,51,78,0.3)",
                          "&.Mui-checked": { color: "#079344" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, color: "#2A334E", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {opt.label}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            ))
            : def.options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                    sx={{
                      color: "rgba(42,51,78,0.3)",
                      "&.Mui-checked": { color: "#079344" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: "#2A334E", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {opt.label}
                  </Typography>
                }
              />
            ))}
        </Box>
      </Popover>
    </>
  );
};

const CandidateInterestTableFilters = ({ filters, onFiltersChange }) => {
  const filterDefs = useMemo(
    () => [
      {
        id: "availability",
        label: "Availability",
        options: AVAILABILITY_OPTIONS,
      },
      {
        id: "countries",
        label: "Country",
        options: COUNTRY_OPTIONS,
      },
      {
        id: "region",
        label: "Region",
        grouped: true,
        groups: [
          {
            label: formatRegionMessage("belgium"),
            options: be_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("luxembourg"),
            options: lu_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("france"),
            options: fr_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
        ],
      },
    ],
    [],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_CANDIDATE_INTEREST_FILTERS });
  }, [onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#F4F5FA",
        borderBottom: "1px solid rgba(42,51,78,0.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "#2A334E", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "16px !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "#D32F2F",
              borderColor: "#D32F2F",
              height: 28,
              borderRadius: "8px",
              "& .MuiChip-deleteIcon": { color: "#D32F2F" },
            }}
            variant="outlined"
          />
        )}

        <Typography
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 12,
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            color: "hsl(150 10% 46%)",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 14, lineHeight: 1 }}>
            *
          </Box>
          A candidate interest is logged when a recruiter reveals a candidate&apos;s profile or downloads their CV.
        </Typography>
      </Stack>
    </Box>
  );
};

export const EMPTY_CANDIDATE_INTEREST_FILTERS = {
  availability: [],
  countries: [],
  region: [],
};

export default CandidateInterestTableFilters;
